import { useState, useEffect } from "react";
import { MessageSquare, X, ArrowRight, User } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function UnreadMessagePopup() {
  const [unread, setUnread] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUnread = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get(`${API}/chat/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Only show messages from coaches/admins
        const coachMsgs = res.data.filter(m =>
          ["coach", "team-admin", "system-admin", "sport-performance-consultant", "school-admin"].includes(m.sender_role)
        );
        if (coachMsgs.length > 0) setUnread(coachMsgs);
      } catch (err) {
        console.error("Failed to check unread:", err);
      }
    };
    // Check after a short delay so dashboard loads first
    const timer = setTimeout(checkUnread, 1500);
    return () => clearTimeout(timer);
  }, []);

  if (dismissed || unread.length === 0) return null;

  const latestMsg = unread[0];
  const uniqueSenders = [...new Map(unread.map(m => [m.sender_id, m])).values()];

  const roleLabel = (role) => {
    const map = { coach: "Coach", "team-admin": "Admin", "system-admin": "Admin", "sport-performance-consultant": "Sport Psychologist", "school-admin": "Admin" };
    return map[role] || "Coach";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white relative">
          <button
            data-testid="dismiss-unread-popup"
            onClick={() => setDismissed(true)}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">New Message{unread.length > 1 ? "s" : ""}</h3>
              <p className="text-white/80 text-sm">
                {unread.length} unread message{unread.length > 1 ? "s" : ""} from {uniqueSenders.length === 1 ? `your ${roleLabel(uniqueSenders[0].sender_role)}` : `${uniqueSenders.length} people`}
              </p>
            </div>
          </div>
        </div>

        {/* Messages Preview */}
        <div className="p-5 space-y-3 max-h-64 overflow-y-auto">
          {unread.slice(0, 3).map((msg) => (
            <div key={msg.id} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{msg.sender_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium">
                    {roleLabel(msg.sender_role)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">{msg.content}</p>
              </div>
            </div>
          ))}
          {unread.length > 3 && (
            <p className="text-xs text-gray-400 text-center">+{unread.length - 3} more message{unread.length - 3 > 1 ? "s" : ""}</p>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex gap-3">
          <Button
            variant="outline"
            onClick={() => setDismissed(true)}
            className="flex-1 text-sm"
          >
            Later
          </Button>
          <Button
            data-testid="go-to-messages-btn"
            onClick={() => { setDismissed(true); navigate("/messages"); }}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
          >
            Open Messages
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
